import { smartSearch } from "../lib/utils";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MainLayout } from "../components/Layout";
import { StatusBadge, REPAIR_STATUSES, getStatusLabel } from "../components/StatusBadge";
import { repairsAPI, clientsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Search, Filter, Wrench, Eye, UserPlus, Camera, AlertCircle, QrCode, Loader2, CheckCircle, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Smartphone brands with IMEI validation
const SMARTPHONE_BRANDS = [
  { value: "apple", label: "Apple", imei_length: 15, serial_length: 10 },
  { value: "samsung", label: "Samsung", imei_length: 15, serial_length: 11 },
  { value: "huawei", label: "Huawei", imei_length: 15, serial_length: 16 },
  { value: "xiaomi", label: "Xiaomi", imei_length: 15, serial_length: 16 },
  { value: "oppo", label: "Oppo", imei_length: 15, serial_length: 16 },
  { value: "vivo", label: "Vivo", imei_length: 15, serial_length: 16 },
  { value: "oneplus", label: "OnePlus", imei_length: 15, serial_length: 16 },
  { value: "google", label: "Google Pixel", imei_length: 15, serial_length: 10 },
  { value: "sony", label: "Sony", imei_length: 15, serial_length: 16 },
  { value: "lg", label: "LG", imei_length: 15, serial_length: 16 },
  { value: "motorola", label: "Motorola", imei_length: 15, serial_length: 16 },
  { value: "nokia", label: "Nokia", imei_length: 15, serial_length: 16 },
  { value: "realme", label: "Realme", imei_length: 15, serial_length: 16 },
  { value: "honor", label: "Honor", imei_length: 15, serial_length: 16 },
  { value: "asus", label: "Asus", imei_length: 15, serial_length: 16 },
  { value: "other", label: "Autre", imei_length: 15, serial_length: null },
];

export default function RepairsPage() {
  const [repairs, setRepairs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    client_id: "",
    device_type: "smartphone",
    device_brand: "",
    device_model: "",
    serial_number: "",
    problem_description: "",
    estimated_cost: "",
    deposit_amount: "",
    notes: "",
  });

  // Photo upload state
  const [devicePhoto, setDevicePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Scanner QR caméra (smartphone)
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [scannerStatus, setScannerStatus] = useState("idle");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);

  // Scanner USB filaire (clavier global)
  const usbScanBuffer = useRef("");
  const usbScanTimer = useRef(null);
  const [usbScanFlash, setUsbScanFlash] = useState(null);
  // QR code photo states
  const [photoQrData, setPhotoQrData] = useState(null);
  const [showPhotoQr, setShowPhotoQr] = useState(false);
  const [photoQrReceived, setPhotoQrReceived] = useState(false);
  const [photoQrPolling, setPhotoQrPolling] = useState(false);
  const photoQrPollRef = useRef(null);
  const [tempRepairId, setTempRepairId] = useState(null);
  
  // Serial number validation
  const [serialError, setSerialError] = useState("");

  // Client search in repair form
  const [clientSearchQuery, setClientSearchQuery] = useState("");

  // New client form
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [newClientData, setNewClientData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    address: "",
  });

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) setStatusFilter(status);
    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [repairsRes, clientsRes] = await Promise.all([
        repairsAPI.list(),
        clientsAPI.list(),
      ]);
      setRepairs(repairsRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Validate serial number based on brand
  const validateSerialNumber = (serial, brand) => {
    if (!serial || !brand) return "";
    
    const brandInfo = SMARTPHONE_BRANDS.find(b => b.value === brand);
    if (!brandInfo || !brandInfo.serial_length) return "";
    
    // For Apple, check serial number (10 chars) or IMEI (15 digits)
    if (brand === "apple") {
      if (serial.length === 10) return ""; // Valid serial
      if (serial.length === 15 && /^\d+$/.test(serial)) return ""; // Valid IMEI
      return `Apple: Numéro de série (10 caractères) ou IMEI (15 chiffres) requis`;
    }
    
    // For Android devices, check IMEI (15 digits)
    if (serial.length !== 15 || !/^\d+$/.test(serial)) {
      return `${brandInfo.label}: IMEI de 15 chiffres requis`;
    }
    
    return "";
  };

  // Handle brand change
  const handleBrandChange = (brand) => {
    setFormData({ ...formData, device_brand: brand, serial_number: "" });
    setSerialError("");
  };

  // Handle serial number change
  const handleSerialChange = (e) => {
    const serial = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setFormData({ ...formData, serial_number: serial });
    setSerialError(validateSerialNumber(serial, formData.device_brand));
  };

  // Handle photo selection
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDevicePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
      // Cancel QR if photo selected manually
      stopPhotoQrPolling();
    }
  };

  // ── Scanner USB filaire — écoute le clavier global ──
  useEffect(() => {
    // Table de correspondance physique (codes US QWERTY → caractères attendus)
    // Un scanner QR envoie toujours des codes US QWERTY quel que soit le layout système
    const US_CODE_MAP = {
      "Digit0":"0","Digit1":"1","Digit2":"2","Digit3":"3","Digit4":"4",
      "Digit5":"5","Digit6":"6","Digit7":"7","Digit8":"8","Digit9":"9",
      "KeyA":"A","KeyB":"B","KeyC":"C","KeyD":"D","KeyE":"E","KeyF":"F",
      "KeyG":"G","KeyH":"H","KeyI":"I","KeyJ":"J","KeyK":"K","KeyL":"L",
      "KeyM":"M","KeyN":"N","KeyO":"O","KeyP":"P","KeyQ":"Q","KeyR":"R",
      "KeyS":"S","KeyT":"T","KeyU":"U","KeyV":"V","KeyW":"W","KeyX":"X",
      "KeyY":"Y","KeyZ":"Z",
      "Minus":"-","Equal":"=","BracketLeft":"[","BracketRight":"]",
      "Backslash":"\\","Semicolon":";","Quote":"'","Backquote":"`",
      "Comma":",","Period":".","Slash":"/",
      // Shift
      "Shift+Digit0":")","Shift+Digit1":"!","Shift+Digit2":"@","Shift+Digit3":"#",
      "Shift+Digit4":"$","Shift+Digit5":"%","Shift+Digit6":"^","Shift+Digit7":"&",
      "Shift+Digit8":"*","Shift+Digit9":"(","Shift+Minus":"_","Shift+Equal":"+",
      "Shift+Semicolon":":","Shift+Quote":"\"","Shift+Comma":"<","Shift+Period":">",
      "Shift+Slash":"?","Shift+BracketLeft":"{","Shift+BracketRight":"}",
      "Space":" ",
    };

    const handleKeyDown = (e) => {
      // Ne pas intercepter si le focus est dans un champ de saisie
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.key === "Enter") {
        const raw = usbScanBuffer.current.trim();
        usbScanBuffer.current = "";
        if (usbScanTimer.current) clearTimeout(usbScanTimer.current);
        if (raw.length < 3) return;

        // Extraire ticket_number — accepte "REP-xxx" direct ou URL "/suivi/REP-xxx"
        let ticket = raw.toUpperCase();
        const urlMatch = raw.match(/\/suivi\/(REP-[A-Z0-9-]+)/i)
                      || raw.match(/(REP-[A-Z0-9-]+)/i);
        if (urlMatch) ticket = urlMatch[1].toUpperCase();

        // Chercher dans les réparations déjà chargées (insensible casse)
        const found = (window.__repairsCache__ || []).find(
          r => r.ticket_number.toUpperCase() === ticket
        );

        if (found) {
          setUsbScanFlash(ticket);
          setTimeout(() => { setUsbScanFlash(null); navigate(`/repairs/${found.id}`); }, 400);
        } else {
          // Fallback API
          const token = localStorage.getItem("token");
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          axios.get(`${API_URL}/api/repairs`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { ticket_number: ticket }
          }).then(res => {
            const list = res.data?.repairs || res.data || [];
            const match = list.find(r => r.ticket_number.toUpperCase() === ticket) || list[0];
            if (match) {
              setUsbScanFlash(ticket);
              setTimeout(() => { setUsbScanFlash(null); navigate(`/repairs/${match.id}`); }, 400);
            } else {
              toast.error(`Réparation "${ticket}" introuvable`);
            }
          }).catch(() => toast.error("Erreur de recherche"));
        }
      } else if (e.key.length === 1 || e.code === "Space") {
        // Utiliser la table de correspondance physique pour corriger le layout clavier
        const codeKey = (e.shiftKey ? "Shift+" : "") + e.code;
        const normalizedChar = US_CODE_MAP[codeKey] || US_CODE_MAP[e.code] || e.key;
        usbScanBuffer.current += normalizedChar;
        if (usbScanTimer.current) clearTimeout(usbScanTimer.current);
        usbScanTimer.current = setTimeout(() => { usbScanBuffer.current = ""; }, 500);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // Cache global pour le scanner USB
  useEffect(() => { window.__repairsCache__ = repairs; }, [repairs]);

  // ── Scanner caméra (smartphone) ──
  const startScanner = async () => {
    setScannerOpen(true);
    setScannerError("");
    setScannerStatus("scanning");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        requestAnimationFrame(scanFrame);
      }
    } catch (err) {
      setScannerError("Impossible d'accéder à la caméra. Vérifiez les permissions.");
      setScannerStatus("error");
    }
  };

  const stopScanner = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setScannerOpen(false);
    setScannerStatus("idle");
    setScannerError("");
  };

  const scanFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      animFrameRef.current = requestAnimationFrame(scanFrame);
      return;
    }
    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    // Essayer BarcodeDetector natif (Chrome/Android)
    if (window.BarcodeDetector) {
      const detector = new window.BarcodeDetector({ formats: ["qr_code", "code_128", "code_39"] });
      detector.detect(canvas).then(barcodes => {
        if (barcodes.length > 0) {
          handleScanResult(barcodes[0].rawValue);
        } else {
          animFrameRef.current = requestAnimationFrame(scanFrame);
        }
      }).catch(() => {
        animFrameRef.current = requestAnimationFrame(scanFrame);
      });
    } else {
      // Fallback jsQR
      if (!window.jsQR) {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js";
        script.onload = () => { animFrameRef.current = requestAnimationFrame(scanFrame); };
        document.head.appendChild(script);
        return;
      }
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = window.jsQR(imageData.data, imageData.width, imageData.height);
      if (code) {
        handleScanResult(code.data);
      } else {
        animFrameRef.current = requestAnimationFrame(scanFrame);
      }
    }
  };

  const handleScanResult = (raw) => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    
    setScannerStatus("found");
    
    // Extraire le ticket_number — accepte soit le ticket direct, soit une URL /suivi/REP-xxx
    let ticket = raw.trim();
    const urlMatch = ticket.match(/\/suivi\/(REP-[A-Z0-9-]+)/i);
    if (urlMatch) ticket = urlMatch[1];
    
    // Chercher dans les réparations chargées
    const found = repairs.find(r =>
      r.ticket_number === ticket ||
      r.ticket_number.toLowerCase() === ticket.toLowerCase()
    );
    
    setTimeout(() => {
      stopScanner();
      if (found) {
        navigate(`/repairs/${found.id}`);
      } else {
        // Pas trouvé localement — essayer via l'API
        const token = localStorage.getItem("token");
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        axios.get(`${API_URL}/api/repairs`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: ticket }
        }).then(res => {
          const results = res.data?.repairs || res.data || [];
          const match = results.find(r => r.ticket_number === ticket);
          if (match) {
            navigate(`/repairs/${match.id}`);
          } else {
            toast.error(`Réparation "${ticket}" introuvable`);
          }
        }).catch(() => toast.error("Erreur lors de la recherche"));
      }
    }, 400);
  };

  const generatePhotoQr = async () => {
    // Need client info to be filled first
    if (!formData.client_id) {
      toast.error("Sélectionnez d'abord un client");
      return;
    }
    if (!formData.device_brand || !formData.device_model) {
      toast.error("Renseignez d'abord la marque et le modèle");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      // Create a temporary repair to get an ID for the token
      const data = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null,
        problem_description: formData.problem_description || "En attente de description",
      };
      const repairRes = await repairsAPI.create(data);
      const repairId = repairRes.data.id;
      setTempRepairId(repairId);

      // Generate mobile upload token
      const res = await axios.post(
        `${API_URL}/api/repairs/${repairId}/mobile-upload-token`,
        null,
        { params: { photo_type: "before" }, headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotoQrData(res.data);
      setShowPhotoQr(true);
      setPhotoQrReceived(false);
      toast.info("Scannez le QR code avec le smartphone pour prendre la photo");
      startPhotoQrPolling(repairId, res.data.token);
    } catch (err) {
      toast.error("Erreur lors de la génération du QR code");
    }
  };

  const startPhotoQrPolling = (repairId, token) => {
    setPhotoQrPolling(true);
    if (photoQrPollRef.current) clearInterval(photoQrPollRef.current);
    photoQrPollRef.current = setInterval(async () => {
      try {
        const authToken = localStorage.getItem("token");
        const res = await axios.get(
          `${API_URL}/api/repairs/${repairId}/photos`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const photos = res.data || [];
        const mobilePhoto = photos.find(p => p.photo_type === "before" && p.source === "mobile_upload");
        if (mobilePhoto) {
          clearInterval(photoQrPollRef.current);
          setPhotoQrPolling(false);
          setPhotoQrReceived(true);
          setShowPhotoQr(false);
          setPhotoPreview(`${API_URL}${mobilePhoto.image_url}`);
          // Mark as already uploaded (no need to re-upload)
          setDevicePhoto("already_uploaded");
          toast.success("📸 Photo reçue depuis le smartphone !");
        }
      } catch (err) { /* ignore */ }
    }, 2000);
  };

  const stopPhotoQrPolling = () => {
    if (photoQrPollRef.current) {
      clearInterval(photoQrPollRef.current);
      photoQrPollRef.current = null;
    }
    setPhotoQrPolling(false);
    setShowPhotoQr(false);
    setPhotoQrData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate photo is required
    if (!devicePhoto) {
      toast.error("Une photo de l'appareil est obligatoire");
      return;
    }
    
    // Validate serial number
    const serialValidation = validateSerialNumber(formData.serial_number, formData.device_brand);
    if (serialValidation) {
      toast.error(serialValidation);
      return;
    }
    
    try {
      const data = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null,
      };

      let repairId;

      if (tempRepairId) {
        // Repair already created via QR flow — just update it with final data
        repairId = tempRepairId;
        await repairsAPI.update(repairId, data);
      } else {
        // Normal flow — create repair then upload photo
        const repairRes = await repairsAPI.create(data);
        repairId = repairRes.data.id;

        // Upload photo
        const photoFormData = new FormData();
        photoFormData.append("file", devicePhoto);
        photoFormData.append("photo_type", "before");
        photoFormData.append("description", "Photo à la réception");

        try {
          await axios.post(`${API_URL}/api/repairs/${repairId}/photos/upload`, photoFormData, {
            headers: {
              "Content-Type": "multipart/form-data",
              "Authorization": `Bearer ${localStorage.getItem("token")}`
            }
          });
        } catch (photoErr) {
          console.error("Erreur upload photo:", photoErr);
          toast.warning("Réparation créée mais la photo n\'a pas pu être uploadée. Vous pouvez l\'ajouter depuis la fiche réparation.");
          setDialogOpen(false);
          resetForm();
          fetchData();
          return;
        }
      }

      stopPhotoQrPolling();
      toast.success("Réparation créée avec succès");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const res = await clientsAPI.create(newClientData);
      toast.success("Client créé");
      setNewClientDialogOpen(false);
      // Add new client to list and select it
      setClients([...clients, res.data]);
      setFormData({ ...formData, client_id: res.data.id });
      setNewClientData({ first_name: "", last_name: "", phone: "", email: "", address: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      device_type: "smartphone",
      device_brand: "",
      device_model: "",
      serial_number: "",
      problem_description: "",
      estimated_cost: "",
      notes: "",
    });
    setClientSearchQuery("");
    setDevicePhoto(null);
    setPhotoPreview(null);
    setSerialError("");
    setPhotoQrData(null);
    setShowPhotoQr(false);
    setPhotoQrReceived(false);
    setPhotoQrPolling(false);
    setTempRepairId(null);
    if (photoQrPollRef.current) { clearInterval(photoQrPollRef.current); photoQrPollRef.current = null; }
  };

  // Filter clients for the select dropdown based on search
  const filteredClientsForSelect = clients.filter((c) =>
    smartSearch(clientSearchQuery, [c.name, c.phone, c.email])
  );

  const filteredRepairs = repairs.filter((r) => {
    const matchesSearch = smartSearch(searchQuery, [
      r.ticket_number, r.client_name, r.device_brand, r.device_model,
      r.problem_description, r.client_phone
    ]);
    const matchesStatus = statusFilter === "all" || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const deviceTypes = [
    { value: "smartphone", label: "Smartphone" },
    { value: "tablet", label: "Tablette" },
    { value: "laptop", label: "Ordinateur portable" },
    { value: "console", label: "Console de jeux" },
    { value: "other", label: "Autre" },
  ];

  return (
    <>
    <MainLayout>
      <div className="space-y-6" data-testid="repairs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-chivo text-3xl font-bold text-foreground">Réparations</h1>
            <p className="text-muted-foreground mt-1">{repairs.length} réparation(s) au total</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-repair-btn" className="bg-primary hover:bg-primary/90 btn-animate">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle réparation
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-chivo">Nouvelle réparation</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Client *</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setNewClientDialogOpen(true)}
                      className="text-primary h-auto py-1 px-2"
                      data-testid="new-client-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Nouveau client
                    </Button>
                  </div>
                  {/* Client search input */}
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher un client (nom, téléphone)..."
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      data-testid="client-search-in-repair"
                      className="pl-10 bg-secondary/50"
                    />
                  </div>
                  <Select
                    value={formData.client_id}
                    onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                    required
                  >
                    <SelectTrigger data-testid="repair-client-select" className="bg-secondary/50">
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {filteredClientsForSelect.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          Aucun client trouvé
                        </div>
                      ) : (
                        filteredClientsForSelect.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} - {c.phone}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {clientSearchQuery && (
                    <p className="text-xs text-muted-foreground">
                      {filteredClientsForSelect.length} client(s) trouvé(s)
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type d'appareil *</Label>
                    <Select
                      value={formData.device_type}
                      onValueChange={(v) => setFormData({ ...formData, device_type: v })}
                    >
                      <SelectTrigger data-testid="repair-device-type-select" className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {deviceTypes.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Marque *</Label>
                    <Select
                      value={formData.device_brand}
                      onValueChange={handleBrandChange}
                      required
                    >
                      <SelectTrigger data-testid="repair-brand-select" className="bg-secondary/50">
                        <SelectValue placeholder="Sélectionner une marque" />
                      </SelectTrigger>
                      <SelectContent>
                        {SMARTPHONE_BRANDS.map((b) => (
                          <SelectItem key={b.value} value={b.value}>
                            {b.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Modèle *</Label>
                    <Input
                      value={formData.device_model}
                      onChange={(e) => setFormData({ ...formData, device_model: e.target.value })}
                      placeholder="iPhone 15, Galaxy S24..."
                      required
                      data-testid="repair-model-input"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>IMEI / N° de série *</Label>
                    <Input
                      value={formData.serial_number}
                      onChange={handleSerialChange}
                      placeholder={formData.device_brand === "apple" ? "10 ou 15 caractères" : "15 chiffres (IMEI)"}
                      required
                      data-testid="repair-serial-input"
                      className={`bg-secondary/50 ${serialError ? "border-red-500" : ""}`}
                    />
                    {serialError && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {serialError}
                      </p>
                    )}
                    {!serialError && formData.device_brand && (
                      <p className="text-xs text-muted-foreground">
                        {formData.device_brand === "apple" 
                          ? "Numéro de série (10 car.) ou IMEI (15 chiffres)"
                          : "IMEI obligatoire (15 chiffres)"}
                      </p>
                    )}
                  </div>
                </div>

                {/* Photo obligatoire */}
                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Photo de l'appareil * <span className="text-xs text-muted-foreground">(obligatoire)</span>
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generatePhotoQr}
                      className="border-primary text-primary text-xs h-7 px-2"
                      title="Prendre la photo depuis un smartphone"
                    >
                      <QrCode className="w-3.5 h-3.5 mr-1" />
                      Via smartphone
                    </Button>
                  </Label>

                  {/* QR Code display */}
                  {showPhotoQr && photoQrData && (
                    <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex flex-col items-center gap-3">
                      <p className="text-xs text-amber-400 font-medium text-center">
                        📱 Scannez ce QR code avec le smartphone pour prendre la photo
                      </p>
                      <div className="bg-white p-3 rounded-lg">
                        <QRCodeSVG value={photoQrData.upload_url} size={130} />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {photoQrPolling ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin text-primary" />
                            <span>En attente de la photo...</span>
                          </>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={stopPhotoQrPolling}
                        className="text-xs text-muted-foreground"
                      >
                        <X className="w-3 h-3 mr-1" /> Annuler
                      </Button>
                    </div>
                  )}

                  {/* Photo reçue via QR */}
                  {photoQrReceived && photoPreview && (
                    <div className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-400">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      Photo reçue depuis le smartphone
                    </div>
                  )}

                  <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary transition-colors">
                    {photoPreview ? (
                      <div className="space-y-2">
                        <img
                          src={photoPreview}
                          alt="Aperçu"
                          className="max-h-40 mx-auto rounded-lg object-cover"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { setDevicePhoto(null); setPhotoPreview(null); setPhotoQrReceived(false); setTempRepairId(null); }}
                        >
                          Changer la photo
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoChange}
                          className="hidden"
                          capture="environment"
                        />
                        <div className="py-4">
                          <Camera className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Cliquez ou prenez une photo
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Ou utilisez le bouton "Via smartphone" ci-dessus
                          </p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description du problème *</Label>
                  <Textarea
                    value={formData.problem_description}
                    onChange={(e) => setFormData({ ...formData, problem_description: e.target.value })}
                    placeholder="Décrivez le problème rencontré..."
                    required
                    data-testid="repair-problem-input"
                    className="bg-secondary/50"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Coût estimé (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                    placeholder="0.00"
                    data-testid="repair-cost-input"
                    className="bg-secondary/50"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Avance versée (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.deposit_amount}
                    onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })}
                    placeholder="0.00 (optionnel)"
                    className="bg-secondary/50"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes internes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notes pour le technicien..."
                    data-testid="repair-notes-input"
                    className="bg-secondary/50"
                    rows={2}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="flex-1">
                    Annuler
                  </Button>
                  <Button type="submit" data-testid="repair-submit-btn" className="flex-1 bg-primary hover:bg-primary/90">
                    Créer la réparation
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Flash scan USB */}
        {usbScanFlash && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl bg-emerald-500 text-white shadow-xl animate-bounce">
            <QrCode className="w-5 h-5" />
            <span className="font-bold text-sm">{usbScanFlash} — Ouverture...</span>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="repair-search-input"
                className="pl-10 bg-secondary/50"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={startScanner}
              title="Scanner un QR code"
              className="shrink-0 border-primary/40 text-primary hover:bg-primary/10"
            >
              <QrCode className="w-4 h-4" />
            </Button>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="repair-status-filter" className="w-48 bg-secondary/50">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {REPAIR_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {getStatusLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Repairs Table / Cards */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : filteredRepairs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune réparation trouvée</p>
          </div>
        ) : (
          <>
            {/* Desktop Table - Hidden on mobile */}
            <div className="hidden md:block bg-card border border-border rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Client</th>
                      <th>Appareil</th>
                      <th>Problème</th>
                      <th>Statut</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRepairs.map((repair) => (
                      <tr key={repair.id} data-testid={`repair-row-${repair.id}`}>
                        <td className="font-mono text-sm text-primary">{repair.ticket_number}</td>
                        <td>
                          <div>
                            <p className="font-medium">{repair.client_name}</p>
                            <p className="text-xs text-muted-foreground">{repair.client_phone}</p>
                          </div>
                        </td>
                        <td>
                          <p>{repair.device_brand} {repair.device_model}</p>
                          <p className="text-xs text-muted-foreground capitalize">{repair.device_type}</p>
                        </td>
                        <td className="max-w-xs">
                          <p className="truncate">{repair.problem_description}</p>
                        </td>
                        <td><StatusBadge status={repair.status} /></td>
                        <td className="text-muted-foreground text-sm">
                          {new Date(repair.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/repairs/${repair.id}`)}
                            data-testid={`view-repair-${repair.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards - Visible only on mobile */}
            <div className="md:hidden space-y-3">
              {filteredRepairs.map((repair) => (
                <div 
                  key={repair.id} 
                  className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/repairs/${repair.id}`)}
                  data-testid={`repair-card-${repair.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-mono text-sm text-primary font-medium">{repair.ticket_number}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(repair.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <StatusBadge status={repair.status} />
                  </div>
                  <div className="mb-2">
                    <p className="font-medium">{repair.client_name}</p>
                    <p className="text-sm text-muted-foreground">{repair.client_phone}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">{repair.device_brand} {repair.device_model}</p>
                      <p className="text-xs text-muted-foreground capitalize">{repair.device_type}</p>
                    </div>
                    <Eye className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{repair.problem_description}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* New Client Dialog */}
        <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo">Nouveau client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input
                    value={newClientData.first_name}
                    onChange={(e) => setNewClientData({ ...newClientData, first_name: e.target.value })}
                    placeholder="Jean"
                    required
                    data-testid="new-client-firstname-input"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={newClientData.last_name}
                    onChange={(e) => setNewClientData({ ...newClientData, last_name: e.target.value })}
                    placeholder="Dupont"
                    required
                    data-testid="new-client-lastname-input"
                    className="bg-secondary/50"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Téléphone *</Label>
                <Input
                  value={newClientData.phone}
                  onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                  placeholder="0696 XX XX XX"
                  required
                  data-testid="new-client-phone-input"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newClientData.email}
                  onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                  placeholder="email@exemple.com"
                  data-testid="new-client-email-input"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input
                  value={newClientData.address}
                  onChange={(e) => setNewClientData({ ...newClientData, address: e.target.value })}
                  placeholder="Adresse du client"
                  data-testid="new-client-address-input"
                  className="bg-secondary/50"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setNewClientDialogOpen(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary hover:bg-primary/90"
                  data-testid="confirm-new-client-btn"
                >
                  Créer le client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>

      {/* ─── Dialog Scanner QR ─── */}
      {scannerOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-primary" />
                <h2 className="text-white font-semibold">Scanner une réparation</h2>
              </div>
              <button onClick={stopScanner} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="relative rounded-2xl overflow-hidden bg-black aspect-square w-full">
              <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-56 h-56">
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-primary rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-primary rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-primary rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-primary rounded-br-lg" />
                  {scannerStatus === "scanning" && (
                    <div className="absolute left-0 right-0 h-0.5 bg-primary shadow-[0_0_8px_2px_rgba(0,145,255,0.6)]" style={{top:"50%", animation:"scanLine 2s ease-in-out infinite"}} />
                  )}
                  {scannerStatus === "found" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/20 rounded-lg">
                      <CheckCircle className="w-16 h-16 text-emerald-400" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <canvas ref={canvasRef} className="hidden" />

            <div className="mt-4 text-center">
              {scannerStatus === "scanning" && (
                <p className="text-white/70 text-sm flex items-center justify-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Pointez vers un QR code d'étiquette
                </p>
              )}
              {scannerStatus === "found" && (
                <p className="text-emerald-400 font-semibold">Réparation trouvée ! Redirection...</p>
              )}
              {scannerStatus === "error" && (
                <p className="text-red-400 text-sm">{scannerError}</p>
              )}
            </div>

            <style>{`
              @keyframes scanLine {
                0%, 100% { top: 10%; }
                50% { top: 90%; }
              }
            `}</style>
          </div>
        </div>
      )}
    </>
  );
}
